import { useRef, useEffect, useState, useCallback } from 'react';

/**
 * `valeurInitiale` (lot L4) : une signature DEJA donnee, que le canevas redessine a son
 * montage. Le cas : le serveur refuse un chantier (numero d'appareil deja pris), le poseur
 * revient corriger l'identifiant puis retourne a la signature. La page a garde le trait
 * du client ; un canevas vide lui ferait croire l'inverse et refaire signer pour rien.
 * Redessiner n'est pas resigner : rien n'est renvoye par `onSignatureChange`.
 */
export default function SignatureCanvas({ onSignatureChange, width = 600, height = 200, valeurInitiale = '' }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [reprise, setReprise] = useState(Boolean(valeurInitiale));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#0F172A';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Draw signature line
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(40, height - 40);
    ctx.lineTo(width - 40, height - 40);
    ctx.strokeStyle = '#CBD5E1';
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#0F172A';
  }, [width, height]);

  // Au montage seulement : la valeur initiale ne doit pas ecraser un trait en cours.
  useEffect(() => {
    if (!valeurInitiale) return undefined;
    let actif = true;
    const image = new Image();
    image.onload = () => {
      const canvas = canvasRef.current;
      if (actif && canvas) canvas.getContext('2d').drawImage(image, 0, 0, width, height);
    };
    image.src = valeurInitiale;
    return () => { actif = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = useCallback((e) => {
    if (e) e.preventDefault();
    if (!isDrawing) return;
    setIsDrawing(false);
    if (hasSignature && onSignatureChange) {
      const canvas = canvasRef.current;
      onSignatureChange(canvas.toDataURL('image/png'));
    }
  }, [isDrawing, hasSignature, onSignatureChange]);

  // Emit signature data when drawing stops
  useEffect(() => {
    if (!isDrawing && hasSignature && onSignatureChange) {
      const canvas = canvasRef.current;
      if (canvas) onSignatureChange(canvas.toDataURL('image/png'));
    }
  }, [isDrawing, hasSignature, onSignatureChange]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Redraw guide line
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(40, height - 40);
    ctx.lineTo(width - 40, height - 40);
    ctx.strokeStyle = '#CBD5E1';
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#0F172A';
    ctx.lineWidth = 2;
    setHasSignature(false);
    setReprise(false);
    if (onSignatureChange) onSignatureChange('');
  };

  return (
    <div className="space-y-2" data-testid="signature-canvas-wrapper">
      <div className="border-2 border-dashed border-slate-300 rounded-lg overflow-hidden bg-white relative">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="w-full cursor-crosshair touch-none"
          style={{ maxHeight: `${height}px` }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          data-testid="signature-canvas"
        />
        {!hasSignature && !reprise && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-slate-300 select-none">Signez ici avec votre doigt ou souris</p>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">
          {hasSignature || reprise ? 'Signature enregistrée' : 'Aucune signature'}
        </p>
        <button
          type="button"
          onClick={clearSignature}
          className="text-xs text-slate-500 hover:text-red-500 underline"
          data-testid="clear-signature-btn"
        >
          Effacer la signature
        </button>
      </div>
    </div>
  );
}
